import { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Plus, Pencil, Trash2, MoreVertical } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  useFetchUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
} from "@/services/query/users.query"

const userFormSchema = z.object({
  email: z.string().email(),
  name: z.string().min(2),
  password: z.string().min(6).optional(),
  user_type: z.string().refine((val) => ["USER", "ADMIN"].includes(val)),
})

function UserManagementPage() {
  const { toast } = useToast()
  const [pagination, setPagination] = useState({ offset: 0, limit: 10 })
  const [search, setSearch] = useState("")
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)

  const { data, isLoading } = useFetchUsers(
    pagination.offset,
    pagination.limit,
    search
  )
  const createUserMutation = useCreateUser()
  const updateUserMutation = useUpdateUser()
  const deleteUserMutation = useDeleteUser()

  const defaultValues = {
    email: "",
    name: "",
    password: "",
    user_type: "USER", // Changed default to string
  }

  const form = useForm({
    resolver: zodResolver(userFormSchema),
    defaultValues,
  })

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value)
      setPagination((prev) => ({ ...prev, offset: 0 }))
    }, 500),
    []
  )

  const handlePageChange = (newOffset) => {
    setPagination((prev) => ({ ...prev, offset: newOffset }))
  }

  const onSubmit = async (values) => {
    try {
      if (selectedUser) {
        const { password, ...updateData } = values
        await updateUserMutation.mutateAsync({
          userId: selectedUser.user_id,
          data: updateData,
        })
        toast({ title: "User updated successfully" })
        setIsEditDialogOpen(false)
      } else {
        await createUserMutation.mutateAsync(values)
        toast({ title: "User created successfully" })
        setIsAddDialogOpen(false)
      }
      form.reset()
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleEdit = (user) => {
    setSelectedUser(user)
    form.reset({
      email: user.email,
      name: user.name,
      user_type: user.user_type,
    })
    setIsEditDialogOpen(true)
  }

  const handleDelete = async () => {
    try {
      await deleteUserMutation.mutateAsync(selectedUser.user_id)
      toast({ title: "User deleted successfully" })
      setIsDeleteDialogOpen(false)
      setSelectedUser(null)
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleAddClick = () => {
    setSelectedUser(null)
    form.reset(defaultValues) // Reset to empty values
    setIsAddDialogOpen(true)
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="w-full sm:w-auto sm:flex-1 max-w-sm">
          <Input
            type="search"
            placeholder="Search users..."
            onChange={(e) => debouncedSearch(e.target.value)}
            className="w-full"
          />
        </div>
        <Button
          onClick={handleAddClick}
          className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600"
        >
          <Plus className="mr-2 h-4 w-4" /> Add User
        </Button>
      </div>

      {/* Responsive table with horizontal scroll on small screens */}
      <div className="relative overflow-x-auto rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="font-semibold">Name</TableHead>
              <TableHead className="font-semibold hidden sm:table-cell">
                Email
              </TableHead>
              <TableHead className="font-semibold">User Type</TableHead>
              <TableHead className="font-semibold hidden md:table-cell">
                Last Login
              </TableHead>
              <TableHead className="w-[60px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array(5)
                .fill(0)
                .map((_, idx) => (
                  <TableRow key={idx} className="animate-pulse">
                    <TableCell className="h-12 bg-gray-100 dark:bg-gray-800" />
                    <TableCell className="hidden sm:table-cell h-12 bg-gray-100 dark:bg-gray-800" />
                    <TableCell className="h-12 bg-gray-100 dark:bg-gray-800" />
                    <TableCell className="hidden md:table-cell h-12 bg-gray-100 dark:bg-gray-800" />
                    <TableCell className="h-12 bg-gray-100 dark:bg-gray-800" />
                  </TableRow>
                ))
            ) : data?.users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              data?.users.map((user) => (
                <TableRow key={user.user_id} className="hover:bg-muted/50">
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <span className="max-w-[200px] truncate block">
                      {user.email}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        user.user_type === "ADMIN"
                          ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30"
                          : "bg-blue-100 text-blue-700 dark:bg-blue-900/30"
                      }`}
                    >
                      {getUserType(user.user_type)}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">
                    {new Date(user.last_login).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px]">
                        <DropdownMenuItem onClick={() => handleEdit(user)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedUser(user)
                            setIsDeleteDialogOpen(true)
                          }}
                          className="text-red-600 dark:text-red-400"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {data && data.pagination.totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <Pagination>
            <PaginationContent className="flex-wrap gap-2">
              <PaginationItem className="hidden sm:block">
                <PaginationPrevious
                  onClick={() =>
                    handlePageChange(
                      Math.max(0, pagination.offset - pagination.limit)
                    )
                  }
                  disabled={pagination.offset === 0}
                />
              </PaginationItem>
              {/* Show fewer pages on mobile */}
              {[...Array(data.pagination.totalPages)].map((_, idx) => (
                <PaginationItem
                  key={idx}
                  className={
                    idx > 2 && idx < data.pagination.totalPages - 1
                      ? "hidden sm:block"
                      : ""
                  }
                >
                  <PaginationLink
                    onClick={() => handlePageChange(idx * pagination.limit)}
                    isActive={pagination.offset === idx * pagination.limit}
                    className="min-w-[40px] justify-center"
                  >
                    {idx + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem className="hidden sm:block">
                <PaginationNext
                  onClick={() =>
                    handlePageChange(pagination.offset + pagination.limit)
                  }
                  disabled={
                    pagination.offset + pagination.limit >=
                    data.pagination.total
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <UserFormDialog
        open={isAddDialogOpen || isEditDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsAddDialogOpen(false)
            setIsEditDialogOpen(false)
            setSelectedUser(null)
          }
        }}
        form={form}
        onSubmit={onSubmit}
        isEdit={!!selectedUser}
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              user's account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function UserFormDialog({ open, onOpenChange, form, onSubmit, isEdit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit User" : "Add New User"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {!isEdit && (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="user_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>User Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select user type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="USER">USER</SelectItem>
                      <SelectItem value="ADMIN">ADMIN</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit">
                {isEdit ? "Update User" : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function getUserType(type) {
  return type || "Unknown" // Since type is already a string, just return it
}

function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export default UserManagementPage
